import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type {
  CommissionStatementRowSourceType,
  CommissionStatementStatus,
  CommissionStatementWithTotals,
  GetCommissionStatementResponse,
} from '@nadoutong/contracts';
import { BRAND_IDS, isCommissionPeriodEnded } from '@nadoutong/contracts';
import { ExternalLink } from 'lucide-react';
import {
  useAppendCommissionAdjustment,
  useCommissionStatement,
  useCommissionStatements,
  useConfirmCommissionStatement,
  useMarkCommissionPayout,
  type CommissionStatementListScope,
} from '../api/commissionSettlement.ts';
import { formatLocalTimestamp } from '../components/formatters.ts';
import { OpsUserRef } from '../components/identity/OpsUserRef.tsx';
import { formatUsdMicros } from '../components/formatters.ts';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import {
  Button,
  Chip,
  ConfirmDialog,
  Drawer,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Pagination,
  Panel,
  Select,
  Table,
  TableEmpty,
  TBody,
  Td,
  Textarea,
  Th,
  THead,
  Tr,
  Skeleton,
  TableSkeleton,
  cx,
  type ChipTone,
} from '../ui/index.ts';

/**
 * Commission settlement queue (epic #1034 E5-M4, #1146) — finance-manager
 * and admin only (the route guard + ops-api both enforce it).
 *
 *   - Queue: per-owner statements filterable by status / brand / period,
 *     each showing the stored (as-confirmed) total, the adjustments delta,
 *     and the displayed total (stored + adjustments).
 *   - Detail drawer: every row (base / clawback / adjustment) with its
 *     source reference + reason, plus the settlement actions —
 *       · confirm (draft only, confirmation dialog);
 *       · append adjustment (confirmed only, signed amount + mandatory
 *         reason);
 *       · record the off-system payout marker once (confirmed + not-yet-
 *         paid, free-text reference).
 *
 * The stored total is immutable once confirmed; adjustments only move the
 * displayed total. Every money figure is server-computed.
 */

const PAGE_SIZE = 50;
const USD_MICROS_PER_USD = 1_000_000;

const STATUS_CHIP_TONE: Record<CommissionStatementStatus, ChipTone> = {
  draft: 'warn',
  confirmed: 'ok',
};

const STATUS_LABEL_KEYS: Record<CommissionStatementStatus, I18nKey> = {
  draft: 'commission_settlement.status.draft',
  confirmed: 'commission_settlement.status.confirmed',
};

const SOURCE_TYPE_LABEL_KEYS: Record<CommissionStatementRowSourceType, I18nKey> = {
  receipt: 'commission_settlement.source_type.receipt',
  payment_order: 'commission_settlement.source_type.payment_order',
  clawback: 'commission_settlement.source_type.clawback',
  adjustment: 'commission_settlement.source_type.adjustment',
};

/** Parse a signed decimal USD string into integer micro-USD; null if invalid. */
function parseUsdToMicros(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^-?\d+(\.\d{1,6})?$/.test(trimmed)) {
    return null;
  }
  const micros = Math.round(Number(trimmed) * USD_MICROS_PER_USD);
  if (!Number.isSafeInteger(micros) || micros === 0) {
    return null;
  }
  return micros;
}

export function CommissionSettlementPage(): JSX.Element {
  const t = useT();
  const [status, setStatus] = useState<CommissionStatementStatus | ''>('');
  const [brand, setBrand] = useState('');
  const [period, setPeriod] = useState('');
  const [offset, setOffset] = useState(0);
  const [detailId, setDetailId] = useState<string | null>(null);

  const scope: CommissionStatementListScope = {
    limit: PAGE_SIZE,
    offset,
    status,
    brand,
    period,
  };
  const query = useCommissionStatements(scope, true);

  const total = query.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const statusOptions = [
    { value: 'all', label: t('commission_settlement.status.all') },
    { value: 'draft', label: t('commission_settlement.status.draft') },
    { value: 'confirmed', label: t('commission_settlement.status.confirmed') },
  ];
  const brandOptions = [
    { value: 'all', label: t('commission_settlement.brand.all') },
    ...BRAND_IDS.map((b) => ({ value: b, label: b })),
  ];

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('commission_settlement.title')}
        sub={t('commission_settlement.subtitle')}
        testId="commission-settlement-header"
      />

      <Panel caption={t('commission_settlement.title')} count={total} testId="commission-settlement-panel">
        <div className="flex flex-wrap items-end gap-3 border-b border-line-soft px-4 py-3">
          <FormField label={t('commission_settlement.filter.status')} fieldId="cs-filter-status">
            <Select
              value={status === '' ? 'all' : status}
              onValueChange={(value) => {
                setStatus(value === 'all' ? '' : (value as CommissionStatementStatus));
                setOffset(0);
              }}
              options={statusOptions}
              ariaLabel={t('commission_settlement.filter.status')}
              testId="commission-settlement-filter-status"
            />
          </FormField>
          <FormField label={t('commission_settlement.filter.brand')} fieldId="cs-filter-brand">
            <Select
              value={brand === '' ? 'all' : brand}
              onValueChange={(value) => {
                setBrand(value === 'all' ? '' : value);
                setOffset(0);
              }}
              options={brandOptions}
              ariaLabel={t('commission_settlement.filter.brand')}
              testId="commission-settlement-filter-brand"
            />
          </FormField>
          <FormField label={t('commission_settlement.filter.period')} fieldId="cs-filter-period">
            <Input
              id="cs-filter-period"
              value={period}
              onChange={(e) => {
                setPeriod(e.target.value);
                setOffset(0);
              }}
              placeholder={t('commission_settlement.filter.period_placeholder')}
              className="w-32"
              data-testid="commission-settlement-filter-period"
            />
          </FormField>
        </div>

        {query.isPending ? <TableSkeleton rows={4} testId="commission-settlement-loading" /> : null}
        {query.isError ? (
          <EmptyState
            title={t('commission_settlement.load_error')}
            hint={coerceErrorMessage(query.error)}
            action={<Button onClick={() => void query.refetch()}>{t('common.retry')}</Button>}
            testId="commission-settlement-error"
          />
        ) : null}
        {query.isSuccess ? (
          <>
            <Table>
              <THead>
                <Tr interactive={false}>
                  <Th>{t('commission_settlement.col.owner')}</Th>
                  <Th>{t('commission_settlement.col.brand')}</Th>
                  <Th>{t('commission_settlement.col.period')}</Th>
                  <Th>{t('commission_settlement.col.status')}</Th>
                  <Th num>{t('commission_settlement.col.stored_total')}</Th>
                  <Th num>{t('commission_settlement.col.adjustments')}</Th>
                  <Th num>{t('commission_settlement.col.displayed_total')}</Th>
                  <Th>{t('commission_settlement.col.payout')}</Th>
                  <Th>{t('commission_settlement.col.actions')}</Th>
                </Tr>
              </THead>
              <TBody>
                {query.data.statements.length === 0 ? (
                  <TableEmpty colSpan={9} testId="commission-settlement-empty">
                    {t('commission_settlement.empty')}
                  </TableEmpty>
                ) : (
                  query.data.statements.map((row) => (
                    <StatementRow key={row.statement.id} row={row} onOpen={() => setDetailId(row.statement.id)} />
                  ))
                )}
              </TBody>
            </Table>
            {pageCount > 1 ? (
              <div className="border-t border-line-soft px-4 py-3">
                <Pagination
                  page={Math.floor(offset / PAGE_SIZE) + 1}
                  pageCount={pageCount}
                  onPageChange={(page) => setOffset((page - 1) * PAGE_SIZE)}
                  prevLabel={t('common.previous')}
                  nextLabel={t('common.next')}
                  ariaLabel={t('commission_settlement.pagination.aria_label')}
                />
              </div>
            ) : null}
          </>
        ) : null}
      </Panel>

      <StatementDetailDrawer id={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}

function StatementRow({
  row,
  onOpen,
}: {
  readonly row: CommissionStatementWithTotals;
  readonly onOpen: () => void;
}): JSX.Element {
  const t = useT();
  const { statement } = row;
  return (
    <Tr>
      <Td mono>
        <OpsUserRef id={statement.ops_user_id} />
      </Td>
      <Td>{statement.brand}</Td>
      <Td mono>{statement.period_start}</Td>
      <Td>
        <Chip tone={STATUS_CHIP_TONE[statement.status]}>{t(STATUS_LABEL_KEYS[statement.status])}</Chip>
      </Td>
      <Td num>{formatUsdMicros(statement.total_usd_micros, false)}</Td>
      <Td num>
        {row.adjustments_total_usd_micros === 0
          ? '—'
          : formatUsdMicros(row.adjustments_total_usd_micros, true)}
      </Td>
      <Td num emphasis>
        {/* Negative = adjustments/clawbacks exceed the confirmed total; keep
            the sign (formatUsdMicros drops it unless `signed`) and flag the
            negative tone so it doesn't read as a rendering bug. */}
        <span
          className={row.displayed_total_usd_micros < 0 ? 'text-bad' : undefined}
          data-testid={`commission-settlement-displayed-${statement.id}`}
        >
          {formatUsdMicros(row.displayed_total_usd_micros, row.displayed_total_usd_micros < 0)}
        </span>
      </Td>
      <Td>
        {statement.paid_out_at !== null ? (
          <Chip tone="ok">{t('commission_settlement.payout.marked')}</Chip>
        ) : (
          <span className="text-paper-low">{t('commission_settlement.payout.pending')}</span>
        )}
      </Td>
      <Td>
        <Button size="sm" variant="ghost" onClick={onOpen} data-testid={`commission-settlement-detail-${statement.id}`}>
          <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
          {t('commission_settlement.action.detail')}
        </Button>
      </Td>
    </Tr>
  );
}

function StatementDetailDrawer({
  id,
  onClose,
}: {
  readonly id: string | null;
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const detail = useCommissionStatement(id);

  return (
    <Drawer
      open={id !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={t('commission_settlement.drawer.title')}
      width="lg"
      closeLabel={t('common.close')}
      testId="commission-settlement-drawer"
    >
      {detail.isPending ? <Skeleton className="h-40 w-full" /> : null}
      {detail.isError ? (
        <EmptyState
          title={t('commission_settlement.load_error')}
          hint={coerceErrorMessage(detail.error)}
          action={<Button onClick={() => void detail.refetch()}>{t('common.retry')}</Button>}
          testId="commission-settlement-drawer-error"
        />
      ) : null}
      {detail.isSuccess ? <StatementDetailBody detail={detail.data} /> : null}
    </Drawer>
  );
}

function StatementDetailBody({
  detail,
}: {
  readonly detail: GetCommissionStatementResponse;
}): JSX.Element {
  const t = useT();
  const { statement } = detail;

  return (
    <div className="flex flex-col gap-5">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-2.5">
        <Field label={t('commission_settlement.drawer.owner')} value={<span className="font-mono"><OpsUserRef id={statement.ops_user_id} /></span>} />
        <Field label={t('commission_settlement.drawer.brand')} value={statement.brand} />
        <Field label={t('commission_settlement.drawer.period')} value={<span className="font-mono">{statement.period_start}</span>} />
        <Field
          label={t('commission_settlement.drawer.status')}
          value={<Chip tone={STATUS_CHIP_TONE[statement.status]}>{t(STATUS_LABEL_KEYS[statement.status])}</Chip>}
        />
        <Field label={t('commission_settlement.drawer.stored_total')} value={<span className="font-mono">{formatUsdMicros(statement.total_usd_micros, false)}</span>} />
        <Field
          label={t('commission_settlement.drawer.adjustments_total')}
          value={<span className="font-mono">{formatUsdMicros(detail.adjustments_total_usd_micros, true)}</span>}
        />
        <Field
          label={t('commission_settlement.drawer.displayed_total')}
          value={
            <span
              className={cx(
                'font-mono',
                detail.displayed_total_usd_micros < 0 ? 'text-bad' : 'text-paper',
              )}
              data-testid="commission-settlement-drawer-displayed"
            >
              {formatUsdMicros(
                detail.displayed_total_usd_micros,
                detail.displayed_total_usd_micros < 0)}
            </span>
          }
        />
        <Field
          label={t('commission_settlement.drawer.payout_marker')}
          value={
            statement.paid_out_at !== null ? (
              <span className="font-mono text-paper-mid" data-testid="commission-settlement-payout-snapshot">
                {statement.paid_out_reference} · {formatLocalTimestamp(statement.paid_out_at)}
                {statement.paid_out_amount_usd_micros === null
                  ? null
                  : ` · ${formatUsdMicros(statement.paid_out_amount_usd_micros, false)}`}
                {statement.paid_out_version === null ? null : ` · v${statement.paid_out_version}`}
              </span>
            ) : (
              <span className="text-paper-low">{t('commission_settlement.drawer.payout_none')}</span>
            )
          }
        />
      </dl>

      {detail.displayed_total_usd_micros < 0 ? (
        <p
          className="m-0 rounded-lg border border-bad-line bg-bad-dim px-3 py-2 text-label text-bad-bright"
          data-testid="commission-settlement-drawer-negative-note"
        >
          {t('commission_settlement.drawer.negative_note')}
        </p>
      ) : null}

      <section className="flex flex-col gap-2">
        <h3 className="m-0 text-section text-paper">{t('commission_settlement.drawer.rows_title')}</h3>
        <Table>
          <THead>
            <Tr interactive={false}>
              <Th>{t('commission_settlement.rows.source_type')}</Th>
              <Th>{t('commission_settlement.rows.reference')}</Th>
              <Th num>{t('commission_settlement.rows.net')}</Th>
              <Th num>{t('commission_settlement.rows.commission')}</Th>
              <Th>{t('commission_settlement.rows.reason')}</Th>
            </Tr>
          </THead>
          <TBody>
            {detail.rows.length === 0 ? (
              <TableEmpty colSpan={5}>{t('commission_settlement.rows.empty')}</TableEmpty>
            ) : (
              detail.rows.map((row) => (
                <Tr key={row.id}>
                  <Td>{t(SOURCE_TYPE_LABEL_KEYS[row.source_type])}</Td>
                  <Td mono>{row.source_id}</Td>
                  <Td num>{row.source_type === 'adjustment' ? '—' : formatUsdMicros(row.net_usd_micros, true)}</Td>
                  <Td num>{formatUsdMicros(row.commission_usd_micros, true)}</Td>
                  <Td>{row.reason ?? <span className="text-paper-low">—</span>}</Td>
                </Tr>
              ))
            )}
          </TBody>
        </Table>
      </section>

      <SettlementActions detail={detail} />
    </div>
  );
}

function Field({ label, value }: { readonly label: string; readonly value: ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-label text-paper-low">{label}</dt>
      <dd className="m-0 text-dense text-paper-mid">{value}</dd>
    </div>
  );
}

function SettlementActions({
  detail,
}: {
  readonly detail: GetCommissionStatementResponse;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { statement } = detail;

  const confirmMutation = useConfirmCommissionStatement();
  const adjustMutation = useAppendCommissionAdjustment();
  const payoutMutation = useMarkCommissionPayout();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [amountError, setAmountError] = useState<string | null>(null);
  const [reference, setReference] = useState('');
  const [payoutConfirmOpen, setPayoutConfirmOpen] = useState(false);
  // End-to-end idempotency key for the adjustment write (bug #1181 R6):
  // stable across retries of the SAME adjustment intent (double click /
  // network retry after an ambiguous failure), regenerated only after a
  // confirmed success so the next adjustment gets a fresh key.
  const adjustKeyRef = useRef<string>(crypto.randomUUID());

  const onConfirm = (): void => {
    confirmMutation.mutate(statement.id, {
      onSuccess: () => {
        toast.success(t('commission_settlement.confirm.toast'));
        setConfirmOpen(false);
      },
      onError: (err) => toast.error(coerceErrorMessage(err)),
    });
  };

  const onAdjust = (): void => {
    const micros = parseUsdToMicros(amount);
    if (micros === null) {
      setAmountError(t('commission_settlement.adjust.invalid_amount'));
      return;
    }
    if (reason.trim().length === 0) {
      return;
    }
    setAmountError(null);
    adjustMutation.mutate(
      {
        id: statement.id,
        body: {
          amount_usd_micros: micros,
          reason: reason.trim(),
          idempotency_key: adjustKeyRef.current,
        },
      },
      {
        onSuccess: () => {
          toast.success(t('commission_settlement.adjust.toast'));
          setAmount('');
          setReason('');
          adjustKeyRef.current = crypto.randomUUID();
        },
        onError: (err) => toast.error(coerceErrorMessage(err)),
      },
    );
  };

  const onPayout = (): void => {
    if (reference.trim().length === 0) {
      return;
    }
    payoutMutation.mutate(
      { id: statement.id, body: { reference: reference.trim() } },
      {
        onSuccess: () => {
          toast.success(t('commission_settlement.payout.toast'));
          setReference('');
          setPayoutConfirmOpen(false);
        },
        onError: (err) => toast.error(coerceErrorMessage(err)),
      },
    );
  };

  if (statement.status === 'draft') {
    // The accrual rebuilds a live draft for the CURRENT open month too;
    // confirming it freezes the period and silently drops every later
    // same-period money event (the platform confirm gate refuses it with
    // PERIOD_OPEN). Block the action here so an operator never fires a
    // request that can only 409.
    const periodEnded = isCommissionPeriodEnded(statement.period_end, Date.now());
    return (
      <section className="flex flex-col gap-2 border-t border-line-soft pt-4">
        <Button
          variant="primary"
          onClick={() => setConfirmOpen(true)}
          loading={confirmMutation.isPending}
          disabled={!periodEnded}
          data-testid="commission-settlement-confirm"
        >
          {t('commission_settlement.confirm.button')}
        </Button>
        {!periodEnded ? (
          <p
            className="m-0 text-label text-paper-low"
            data-testid="commission-settlement-confirm-period-open"
          >
            {t('commission_settlement.confirm.period_open')}
          </p>
        ) : null}
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title={t('commission_settlement.confirm.title')}
          body={t('commission_settlement.confirm.body')}
          confirmLabel={t('commission_settlement.confirm.cta')}
          cancelLabel={t('common.cancel')}
          closeLabel={t('common.close')}
          onConfirm={onConfirm}
          loading={confirmMutation.isPending}
          testId="commission-settlement-confirm-dialog"
        />
      </section>
    );
  }

  // Confirmed: adjustment form + payout marker form.
  return (
    <div className="flex flex-col gap-5 border-t border-line-soft pt-4">
      {statement.paid_out_at !== null ? (
        <section className="flex flex-col gap-2" data-testid="commission-settlement-adjust-locked">
          <h3 className="m-0 text-section text-paper">{t('commission_settlement.adjust.title')}</h3>
          <p className="m-0 text-dense text-paper-low">
            {t('commission_settlement.payout.done_note')}
          </p>
        </section>
      ) : (
        <section className="flex flex-col gap-3">
          <h3 className="m-0 text-section text-paper">{t('commission_settlement.adjust.title')}</h3>
          <FormField
            label={t('commission_settlement.adjust.amount_label')}
            fieldId="cs-adjust-amount"
            required
            hint={t('commission_settlement.adjust.amount_hint')}
            error={amountError}
          >
            <Input
              id="cs-adjust-amount"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setAmountError(null);
              }}
              placeholder="-3.50"
              className="w-40"
              data-testid="commission-settlement-adjust-amount"
            />
          </FormField>
          <FormField label={t('commission_settlement.adjust.reason_label')} fieldId="cs-adjust-reason" required>
            <Textarea
              id="cs-adjust-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('commission_settlement.adjust.reason_placeholder')}
              rows={2}
              data-testid="commission-settlement-adjust-reason"
            />
          </FormField>
          <div>
            <Button
              variant="secondary"
              onClick={onAdjust}
              loading={adjustMutation.isPending}
              disabled={amount.trim().length === 0 || reason.trim().length === 0}
              data-testid="commission-settlement-adjust-submit"
            >
              {t('commission_settlement.adjust.submit')}
            </Button>
          </div>
        </section>
      )}

      <section className="flex flex-col gap-3">
        <h3 className="m-0 text-section text-paper">{t('commission_settlement.payout.title')}</h3>
        {statement.paid_out_at !== null ? (
          <p className="m-0 text-dense text-paper-low">{t('commission_settlement.payout.done_note')}</p>
        ) : (
          <>
            <FormField label={t('commission_settlement.payout.reference_label')} fieldId="cs-payout-reference" required>
              <Input
                id="cs-payout-reference"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder={t('commission_settlement.payout.reference_placeholder')}
                data-testid="commission-settlement-payout-reference"
              />
            </FormField>
            <div>
              <Button
                variant="secondary"
                onClick={() => setPayoutConfirmOpen(true)}
                loading={payoutMutation.isPending}
                disabled={reference.trim().length === 0}
                data-testid="commission-settlement-payout-submit"
              >
                {t('commission_settlement.payout.submit')}
              </Button>
            </div>
            {/* The payout marker is set-once (paid_out_at IS NULL gate) — a
                mis-click is unrecoverable through the UI, so gate it behind
                the same confirmation dialog as confirm/lock. */}
            <ConfirmDialog
              open={payoutConfirmOpen}
              onOpenChange={setPayoutConfirmOpen}
              title={t('commission_settlement.payout.confirm_title')}
              body={t('commission_settlement.payout.confirm_body')}
              confirmLabel={t('commission_settlement.payout.confirm_cta')}
              cancelLabel={t('common.cancel')}
              closeLabel={t('common.close')}
              onConfirm={onPayout}
              loading={payoutMutation.isPending}
              testId="commission-settlement-payout-dialog"
            />
          </>
        )}
      </section>
    </div>
  );
}
